import { useEffect, useState } from 'react';
import HeroSlider from '../components/HeroSlider';
import HeroBookingWidget from '../components/booking/HeroBookingWidget';
import GoldBadge from '../components/ui/GoldBadge';
import Button from '../components/ui/Button';
import StatCard from '../components/ui/StatCard';
import SectionHeading, { Eyebrow } from '../components/ui/SectionHeading';
import MediaImage from '../components/ui/MediaImage';
import DistanceList from '../components/ui/DistanceList';
import CTASection from '../components/ui/CTASection';
import Reveal from '../components/ui/Reveal';
import VideoBlock from '../components/VideoBlock';
import HomePreviewCarousel from '../components/HomePreviewCarousel';
import Seo from '../components/Seo';
import { usePageSeoContext } from '../lib/PageSeoContext';
import { fetchHomeBootstrap } from '../lib/bootstrapApi';
import type { HeroSlide } from '../lib/heroSlidesApi';
import { HERO_SLIDE_HOLD_SECONDS_KEY, DEFAULT_HERO_SLIDE_HOLD_SECONDS } from '../lib/siteSettingsApi';
import type { TrustStat } from '../lib/trustStatsApi';
import type { DistanceItem } from '../lib/distancesApi';
import { fetchHomePreviews, type HomePreview } from '../lib/homePreviewsApi';
import { useSectionVisible } from '../lib/pageSections';

const whyChooseUs = [
  { title: 'Prime Location', description: '5 min to Kankaria Lake, 15 min to the railway station, 35 min to the airport.' },
  { title: 'Business Ready', description: 'Complimentary 300+ Mbps Wi-Fi, a proper work desk, and stationery on hand.' },
  { title: 'Full Kitchen', description: 'Cook for your family in a private, fully-equipped kitchen — no eating out required.' },
  { title: 'Safe & Secure', description: 'CCTV, digital locks, and Superhost-grade safety standards throughout.' },
];

const storyPoints = [
  { strong: 'Guaranteed exclusivity', text: 'you book the entire floor.' },
  { strong: 'Family & child friendly', text: 'a full kitchen for home-cooked meals.' },
  { strong: 'Superior hygiene', text: 'rigorous cleaning between every stay.' },
  { strong: 'Smart convenience', text: 'no split hotel rooms, your whole group stays together.' },
];

export default function Home() {
  const { siteSettings } = usePageSeoContext();
  const [heroSlides, setHeroSlides] = useState<HeroSlide[]>([]);
  const [trustStats, setTrustStats] = useState<TrustStat[]>([]);
  const [nearby, setNearby] = useState<DistanceItem[]>([]);
  const [spacesPreview, setSpacesPreview] = useState<HomePreview[]>([]);

  const trustStripVisible = useSectionVisible('home.trust_strip');
  const whyChooseUsVisible = useSectionVisible('home.why_choose_us');
  const storyVisible = useSectionVisible('home.story');
  const spacesPreviewVisible = useSectionVisible('home.spaces_preview');
  const videoVisible = useSectionVisible('home.video');
  const mapVisible = useSectionVisible('home.map');
  const ctaVisible = useSectionVisible('home.cta');

  useEffect(() => {
    fetchHomeBootstrap()
      .then((data) => {
        setHeroSlides(data.heroSlides);
        setTrustStats(data.trustStats);
        setNearby(data.distances);
      })
      .catch(() => {});
    fetchHomePreviews().then(setSpacesPreview).catch(() => {});
  }, []);

  const rawHoldSeconds = Number(siteSettings[HERO_SLIDE_HOLD_SECONDS_KEY]);
  const holdSeconds = Number.isFinite(rawHoldSeconds) && rawHoldSeconds > 0 ? rawHoldSeconds : DEFAULT_HERO_SLIDE_HOLD_SECONDS;

  return (
    <div>
      <Seo
        pageKey="home"
        fallbackTitle="Homeland Stay — Gold Category Private Floor Homestay, Ahmedabad"
        fallbackDescription="A Gold Category private-floor homestay near Kankaria Lake, Ahmedabad. An entire first floor, exclusively yours."
      />
      {/* Hero — md+ height is viewport-relative (75vh, clamped) rather than a
          flat 760px so laptops with a shorter viewport (lower-density/lower-
          resolution screens, where 760px can exceed the whole visible page)
          get a proportionally shorter hero instead of an over-cropped,
          "zoomed in" object-cover image. min-h must comfortably fit the
          badge+title+subtitle+buttons stack in SlideContent (bottom-anchored
          via justify-end inside this overflow-hidden section) — too short a
          floor here clips the badge off the top instead of shrinking the
          image crop. */}
      <section className="relative h-[560px] md:h-[75vh] md:min-h-[600px] md:max-h-[700px] overflow-hidden bg-ink">
        <HeroSlider
          slides={heroSlides}
          holdSeconds={holdSeconds}
          badges={<GoldBadge logo="/gujarat-tourism-logo.webp">Gujarat Tourism — Gold Category</GoldBadge>}
        />
      </section>

      {/* Booking widget — a single instance whose wrapper is repositioned by
          breakpoint rather than rendered twice, so no state (typed name,
          picked dates…) is ever lost across a resize. Below md it's a normal-
          flow block, pulled up over the hero's bottom edge with a negative
          margin. At md (tablet) it switches to absolute but sits flush with
          this same max-w-page box's own top-right corner (which is exactly
          where the hero ends) — top-8 matches the trust strip's own pt-8 so
          it lines up with the first stat card instead of overlapping the
          hero image, and it's sized to 60% of the box width (the stats grid
          below takes the other 40%, minus a shared 16px half-gap each side)
          so the two form a deliberate 60/40 split rather than a fixed-width
          widget with whatever's left over. At lg+ it goes back to
          overlapping the hero by a fixed -top-24 amount at a fixed 480px
          (not stretched to the widget's own height, which is what used to
          leave it either overlapping the CTA buttons or floating
          disconnected from the image depending on viewport). */}
      <div className="relative z-20 px-4 md:px-8">
        <div className="max-w-page mx-auto relative">
          <div className="relative -mt-16 pb-6 flex justify-center md:absolute md:mt-0 md:pb-0 md:right-0 md:top-8 lg:-top-24 md:block md:justify-normal md:w-[calc(60%-16px)] lg:w-[480px]">
            <HeroBookingWidget />
          </div>
        </div>
      </div>

      {/* Trust strip — starts exactly at the hero's true bottom edge (no
          negative margin here) so its dark backdrop never paints over the
          hero image; the widget above overlaps it visually via its own
          absolute positioning without affecting this section's own layout.
          The reserved max-w matches the widget's own two size tiers — a 40%
          share (mirroring the widget's 60%, same 16px half-gap) at md, a
          fixed 480px+gap reservation at lg — so the stats grid never gets
          squeezed under a reservation sized for the wider desktop widget.
          A single column at md (rather than keeping 2, as at both the
          narrower mobile-stacked layout and the wider lg one) — the reserved
          width there is too narrow to fit 2 columns of the gold card's
          icon-beside-text layout without crushing it illegibly; one column
          gives each card the full reserved width instead. */}
      {trustStripVisible && (
        <div className="relative z-10 bg-ink px-4 md:px-8 pt-8 pb-10 md:pb-24">
          <div className="max-w-page mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-4 md:max-w-[calc(40%-16px)] lg:max-w-[calc(100%-528px)]">
              {trustStats.map((stat, i) => (
                <Reveal key={stat.id} delay={i * 0.06}>
                  <StatCard variant={stat.variant} value={stat.value} label={stat.label} link={stat.link} confirmLeave={!!stat.confirm_leave} />
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Why guests choose us */}
      {whyChooseUsVisible && (
        <section className="max-w-page mx-auto px-8 py-16 md:py-24">
          <SectionHeading
            align="center"
            eyebrow="Why guests choose us"
            title="We combine the amenities of a luxury hotel with the comfort of home"
            className="mb-14"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-hairline border border-hairline">
            {whyChooseUs.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.08} className="hs-card bg-paper p-9">
                <div className="font-serif text-[19px] text-ink mb-2.5">{item.title}</div>
                <p className="text-[14.5px] leading-relaxed text-muted m-0">{item.description}</p>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Story */}
      {storyVisible && (
        <section className="bg-sand py-16 md:py-24">
          <div className="max-w-page mx-auto px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-18 items-center">
            <Reveal>
              <Eyebrow>The top choice for groups &amp; families</Eyebrow>
              <h2 className="font-serif font-medium text-[28px] md:text-[32px] text-ink m-0 mb-5 leading-tight">
                The best luxury homestay near Kankaria Lake
              </h2>
              <p className="text-base leading-loose text-ink-soft m-0 mb-7">
                Unlike a standard hotel, Homeland Stay is an exclusive first-floor experience — your group books the
                whole floor, with no shared walls and no strangers. Whether you're a family that needs a
                child-friendly kitchen or a corporate group that needs a quiet place to work, you get a true
                home-away-from-home.
              </p>
              <div className="grid gap-3.5">
                {storyPoints.map((point) => (
                  <div key={point.strong} className="flex gap-3 items-baseline">
                    <span className="text-olive font-serif">—</span>
                    <span className="text-[15px] text-ink-soft-2">
                      <strong className="text-ink">{point.strong}</strong> — {point.text}
                    </span>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal>
              <MediaImage slotKey="home.entrance" label="photo — private secure entrance" height={480} className="rounded" />
            </Reveal>
          </div>
        </section>
      )}

      {/* Spaces preview */}
      {spacesPreviewVisible && (
        <section className="py-16 md:py-24">
          <SectionHeading
            eyebrow="The spaces"
            title="Everywhere you'll spend your stay"
            className="max-w-page mx-auto px-8 mb-10"
            action={
              <Button variant="text" to="/rooms">
                See all rooms &amp; suites →
              </Button>
            }
          />
          <HomePreviewCarousel previews={spacesPreview} />
        </section>
      )}

      {videoVisible && (
        <VideoBlock
          title="See Guest Stay Experiences at Homeland Stay"
          description="Watch real video interviews and hear why our guests love their stay with us."
          label="video — guest interviews"
          slotKey="home.video"
          size="lg"
        />
      )}

      {/* Map */}
      {mapVisible && (
        <section className="max-w-page mx-auto px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <Reveal>
            <MediaImage slotKey="home.map" label="map — Bhairavnath Road, Nr. Kankaria Lake" height={380} />
          </Reveal>
          <Reveal>
            <Eyebrow>Centrally located</Eyebrow>
            <h2 className="font-serif font-medium text-[26px] md:text-[30px] text-ink m-0 mb-5.5">
              Everything nearby, peace all around
            </h2>
            <DistanceList items={nearby} columns={2} />
            <Button variant="text" to="/location" className="inline-block mt-6">
              Get directions →
            </Button>
          </Reveal>
        </section>
      )}

      {ctaVisible && (
        <CTASection
          title="Ready for a stay that feels like home?"
          description="Reserve your private floor at Homeland Stay today."
        />
      )}
    </div>
  );
}
